import CrumbRoute from 'components/CrumbRoute/CrumbRoute';
import Layout from 'components/Layout/Layout';
import Loading from 'components/Loading/Loading';
import i18next from 'i18next';
import React, { useEffect, lazy, Suspense } from 'react';
import { registerLocale, setDefaultLocale } from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import { pdfjs } from 'react-pdf';
import { useDispatch, useSelector } from 'react-redux';
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  withRouter,
} from 'react-router-dom';
import { loadLatestActiveProjectsOfUser } from 'store/projects/thunks/loadLatestActiveProjectsOfUser.thunk';
import { loadUserProjectsAndResources } from 'store/projects/thunks/project.thunks';
import { loadPersonalClockedInRecords } from 'store/records/thunks/loadPersonalClockedInRecords.thunk';
import loadComponent from 'utils/loadComponent';

import { getChatControlData } from 'components/ChatDialog/store/slices/chatSlice';
import { getProjectThreadsThunk } from 'components/ChatDialog/store/thunks/getProjectThreads';
import NotificationSnackbar from 'components/NotificationSnackbar/NotificationSnackbar';
import { resumeStoredProjectBankArchiveJobs } from 'store/projectBankArchiveJobs/thunks/projectBankArchiveJobs.thunks';
import { loadCurrentUserCards } from 'store/cards/thunks/loadCurrentUserCards.thunk';
import { openNotificationsSnackbar } from 'store/notificationSnackbar/actions/notificationSnackbar.actions';
import { loadLoggedInUserNotifications } from 'store/notifications/thunks/loadLoggedInUserNotifications.thunk';
import { loadUserRecordsFromToday } from 'store/records/thunks/loadUserRecordsFromToday.thunk';
import { selectCurrentUserActiveEmployerProject } from 'store/resources/selectors/resources.selectors';
import { selectUserActiveEmployerInfo } from 'store/user/selectors.js/user.selectors';
import { ImageCanvas } from './AreaPlanning/ImageCanvas/ImageCanvas';
import { loadAllCurrentUserPermissions } from './Permissions/store/thunks/loadAllCurrentUserPermissions.thunk';
import { BuyChatbot } from './Settings/BuyChatbot/BuyChatbot';
import { FormEditor } from './Settings/FormEditor/FormEditor';
import { FormEditorForms } from './Settings/FormEditor/FormEditorForms';
import { getHaveIAcceptedChatbotTermsThunk } from './Settings/Chatbot/store/thunks/chatbotThunks';
import { connectPrivateWebsocketThunk } from 'components/ChatDialog/store/thunks/connectPrivateWebsocket';
import { getPrivateThreadsThunk } from 'components/ChatDialog/store/thunks/getPrivateThreads';
import { useChatNotifications } from 'hooks/useChatNotifications';
import { useMeetingNotifications } from 'hooks/useMeetingNotifications';
import { useEmailNotifications } from 'hooks/useEmailNotifications';
import { useChatWebSocket } from 'hooks/useChatWebSocket';
import { useProjectEmailWebSocket } from 'hooks/useProjectEmailWebSocket';
import { selectProjectEmailDialog } from 'store/projectEmailDialog/projectEmailDialog.slice';

import 'react-quill/dist/quill.snow.css';
import { getUnreadMessagesCountThunk } from 'store/projectEmail/thunks/getUnreadMessagesCount.thunk';
import isFeatureEnabled, { Feature } from 'utils/isFeatureEnabled';
import { loadCurrentUserWorkTimeSaldoInformation } from 'store/user/thunks/loadUserWorkTimeSaldoInformation.thunk';

const LoadableBulletinBoard = loadComponent(() => import('./BulletinBoard'));
const LoadableTimelines = loadComponent(() => import('./Timelines'));
const LoadableInfo = loadComponent(() => import('./Info'));
const LoadableProject = loadComponent(() => import('./Project'));
const LoadableProjects = loadComponent(() => import('./Projects'));
const LoadableProjectBank = loadComponent(() => import('./ProjectBank'));
const LoadablePaymentReturn = loadComponent(() => import('./PaymentReturn'));
const LoadableProjectCreate = loadComponent(() => import('./ProjectCreateNew'));
const LoadableProjectGallery = loadComponent(() => import('./ProjectGallery'));
const LoadableProjectJoin = loadComponent(() => import('./ProjectJoin'));
const LoadableSettings = loadComponent(() => import('./Settings'));
const LoadableWorkDemo = loadComponent(() => import('./WorkDemo/WorkDemo'));
const LoadablePersonalWorkDemo = loadComponent(
  () => import('./WorkDemo/PersonalWorkDemo')
);
const LoadableProjectRedirect = loadComponent(
  () => import('./ProjectRedirect')
);
const LoadableTracking = loadComponent(() => import('./ExpenseTracking'));
const LoadableProjectForms = loadComponent(() => import('./ProjectForms'));
const LoadableNonConformanceReports = loadComponent(
  () => import('./NonConformanceReports')
);
const LoadablePermissions = loadComponent(() => import('./Permissions'));

const LoadableUserProfile = loadComponent(() => import('./UserProfile'));
const LoadableDefectList = loadComponent(() => import('./DefectList'));
const LoadableShortTermProjects = loadComponent(
  () => import('./ShortTermProjects')
);
const LoadableOrganizationChart = loadComponent(
  () => import('./OrganizationChart')
);

const LoadableProjectResources = loadComponent(
  () => import('./ProjectResources')
);

const LoadableFilingsDashboard = loadComponent(() => import('./Filings'));
const LoadableAreaPlanning = loadComponent(() => import('./AreaPlanning'));
const LoadableProjectEmail = loadComponent(() => import('./ProjectEmail'));
const LoadableAdmin = loadComponent(() => import('./Admin'));
const LoadableRenolog = loadComponent(() => import('./Renolog'));
const LoadableKanban = loadComponent(() => import('./Kanban'));
const LoadableRenologAdmin = loadComponent(
  () => import('./Renolog/components/RenologAdminPage')
);
const LoadableAdminAi = loadComponent(() => import('./AdminAi'));
const ChatDialog = lazy(() => import('components/ChatDialog/ChatDialog'));
// Global overlay for the same reason as ChatDialog: one conversation, reachable
// from a launcher in any scene, and it must survive following its own links.
const NerobottiPanelHost = lazy(() =>
  import('scenes/Nerobotti/NerobottiPanelHost')
);
// The project email composer as an overlay: any scene opens it through
// openProjectEmailDialog and keeps the user where they are. Mounted only
// while open, so its editor is not loaded before the first use.
const ProjectEmailDialog = lazy(
  () => import('components/ProjectEmailDialog/ProjectEmailDialog')
);

function App(props) {
  const dispatch = useDispatch();

  // Project bank archive jobs left behind by a reload or a closed app are
  // picked up again: a finished one is offered for download, a running one
  // keeps its progress snackbar.
  useEffect(() => {
    dispatch(resumeStoredProjectBankArchiveJobs());
  }, []);
  const currentUserActiveEmployerProject = useSelector(
    selectCurrentUserActiveEmployerProject
  );
  const activeEmployerInfo = useSelector(selectUserActiveEmployerInfo);
  const { isOpen: showProjectEmailDialog } = useSelector(
    selectProjectEmailDialog
  );

  const { t } = useTranslation('common');

  const language = i18next.language;
  if (language === 'en') {
    const locale = require(`date-fns/locale/en-US`);
    registerLocale(language, locale.default);
    setDefaultLocale(language);
  } else {
    try {
      const locale = require(`date-fns/locale/${language}`);
      registerLocale(language, locale.default);
      setDefaultLocale(language);
    } catch (error) {
      // Fallback to en-US if locale is not found
      const locale = require(`date-fns/locale/en-US`);
      registerLocale('en-US', locale.default);
      setDefaultLocale('en-US');
    }
  }

  // let crumbpath = null;
  pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

  const history = useHistory();

  const user = useSelector((state) => state.root.user);
  const { chatWebsocket } = useSelector(getChatControlData);

  // Handle chat and video call notifications
  useChatNotifications();

  // Fire meeting reminders (15 min before start + at start)
  useMeetingNotifications();

  // Handle email notifications
  useEmailNotifications();

  // WebSocket connections with automatic resubscription
  const { chatWebsocketRef, storeThreads } = useChatWebSocket();
  const { subscribeToProjects } = useProjectEmailWebSocket(user);

  useEffect(() => {
    dispatch(loadUserProjectsAndResources(history)).then((result) => {
      const projects = result.payload?.projects || [];
      const projectsWithProjectChatEnabled = projects.filter((project) =>
        isFeatureEnabled(Feature.PROJECT_CHAT, project)
      );
      dispatch(
        getProjectThreadsThunk({
          projects: projectsWithProjectChatEnabled,
          chatWebsocketRef,
          userId: user.id,
        })
      ).then((threadsResult) => {
        // Store threads for resubscription on reconnect
        const threads = threadsResult.payload?.threads || {};
        storeThreads(Object.values(threads));
      });
    });
    dispatch(loadUserRecordsFromToday());
    dispatch(loadPersonalClockedInRecords());
    dispatch(loadAllCurrentUserPermissions());
    dispatch(loadCurrentUserCards());
    dispatch(loadCurrentUserWorkTimeSaldoInformation());
    dispatch(loadLoggedInUserNotifications());
    // Chatbot licences are a system-admin-only read; the ChatbotAnalytics page
    // fetches them on its own mount. Fetching here 403'd for every other user.
    dispatch(getHaveIAcceptedChatbotTermsThunk());
    dispatch(getPrivateThreadsThunk());
    dispatch(getUnreadMessagesCountThunk()).then((result) => {
      // Subscribe to project email notifications for all projects
      const unreadCounts = result.payload || {};
      const projectKeys = Object.keys(unreadCounts);
      subscribeToProjects(projectKeys);
    });
  }, []);

  // The 30-day "Latest projects" data (any-activity + record-derived id sets,
  // one request) feeds both the navbar Timer drawer and the Projects scene.
  // It is employer-scoped, so load it (and refetch on active-employer change)
  // in a dedicated effect. The thunk dedupes via its `condition`, so re-runs
  // are cheap.
  useEffect(() => {
    dispatch(loadLatestActiveProjectsOfUser({ activeEmployerInfo }));
  }, [activeEmployerInfo]);

  useEffect(() => {
    if (!chatWebsocketRef.current || !user?.id) return;

    const ws = chatWebsocketRef.current;

    if (ws.readyState === WebSocket.OPEN) {
      dispatch(
        connectPrivateWebsocketThunk({ chatWebsocketRef, userId: user.id })
      );
    } else if (ws.readyState === WebSocket.CONNECTING) {
      const handleOpen = () => {
        dispatch(
          connectPrivateWebsocketThunk({ chatWebsocketRef, userId: user.id })
        );
      };
      ws.addEventListener('open', handleOpen);
      return () => {
        ws.removeEventListener('open', handleOpen);
      };
    }
  }, [chatWebsocket, user?.id]);

  // Add an event listener to detect when the new service worker is activated
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    window.location.reload();
  });

  window.addEventListener('message', (event) => {
    if (event.data.type === 'swInstall') {
      console.log('swInstall listener');

      dispatch(
        openNotificationsSnackbar({
          message: 'common.app_update',
          type: 'success',
        })
      );
    }
  });

  return (
    <Layout>
      {/* <Breadcrumbs className={classes.breadcrumbs} />  BREADCRUMBS MOVED TO APPBAR.JS*/}
      {/*<CrumbRouteTwo />*/}

      <CrumbRoute path="/" title="MyLixani" />

      <Route exact path="/">
        {currentUserActiveEmployerProject ? (
          <Redirect
            to={`/project/${currentUserActiveEmployerProject.projectKey}${history.location.search}`}
          />
        ) : (
          <LoadableProjects />
        )}
      </Route>
      <Route path="/loading" component={Loading} />
      {/* Where a payer lands back from their bank after approving a payment. */}
      <Route path="/payment-return" render={() => <LoadablePaymentReturn />} />
      <CrumbRoute
        path="/projects"
        title={t('navigation.projects')}
        render={() => <LoadableProjects />}
      />
      <CrumbRoute path="/project/:project" crumbpath={true} />

      {/* <CrumbRoute
        path="/bank/:parent"
        exact
        component={LoadableProjectBank}
        title={t('dashboard.project_bank')}
      /> */}
      <CrumbRoute
        path="/bank/"
        exact
        component={LoadableProjectBank}
        title={t('dashboard.project_bank')}
      />
      <CrumbRoute
        path="/info/"
        exact
        component={LoadableInfo}
        title={t('dashboard.info')}
      />
      <CrumbRoute
        path="/settings/"
        exact
        component={LoadableSettings}
        title={t('dashboard.settings')}
      />
      <CrumbRoute
        path="/settings/:idx/"
        exact
        component={LoadableSettings}
        title={t('dashboard.settings')}
      />
      <CrumbRoute
        path="/settings/:idx/:subidx"
        exact
        component={LoadableSettings}
        title={t('dashboard.settings')}
      />
      <Route path="/project" exact render={() => <LoadableProjects />} />

      <Route exact path="/:project">
        {Number.isInteger(Number(props.location.pathname.split('/')[1])) && ( // Checks if the path is number aka projectKey -> must have for crumbroutes to work with parent projects
          <Redirect to={'/project' + props.location.pathname} />
        )}
      </Route>
      <CrumbRoute path="/admin" title={'Admin'} />
      <Switch>
        <CrumbRoute
          path="/project/:project/calendar/:projectId"
          exact
          component={LoadableWorkDemo}
          title={t('navigation.calendar')}
        />
        <CrumbRoute
          path="/mylixani/calendar/"
          exact
          component={LoadablePersonalWorkDemo}
          title={t('navigation.calendar')}
        />
        <CrumbRoute
          path="/project/:projectKey/organization-chart/:projectId"
          exact
          component={LoadableOrganizationChart}
          title={'Organisaatiokaavio'}
        ></CrumbRoute>
        <CrumbRoute
          path="/project/:project/create/:parent_project"
          component={LoadableProjectCreate}
          title={t('dashboard.create_project')}
        />
        <CrumbRoute
          path="/project/create/"
          component={LoadableProjectCreate}
          title={t('dashboard.create_project')}
        />
        <CrumbRoute
          path="/project/join/:project"
          component={LoadableProjectJoin}
          title={t('dashboard.join_project')}
        />
        <CrumbRoute
          path="/project/join"
          component={LoadableProjectJoin}
          title={t('dashboard.join_project')}
        />
        <CrumbRoute
          path="/project/:project/gallery/:projectId"
          component={LoadableProjectGallery}
          title={t('gallery.gallery')}
        />
        <CrumbRoute
          path="/project/:project/:projectId/bank/"
          exact
          component={LoadableProjectBank}
          title={t('dashboard.project_bank')}
        />
        {/* <CrumbRoute
          path="/project/:project/:projectId/bank/:parent"
          component={LoadableProjectBank}
          title={t('dashboard.project_bank')}
        /> */}
        <CrumbRoute
          path="/project/:project/projectforms/:projectId"
          component={LoadableProjectForms}
          title={t('navigation.forms')}
        />
        <CrumbRoute
          path="/project/:project/nonconformancereports/:projectId"
          component={LoadableNonConformanceReports}
          title={t('dashboard.non_conformance_reports')}
        />
        <CrumbRoute
          path="/project/:project/defects/:projectId"
          component={LoadableDefectList}
          title={t('navigation.lists')}
        />
        <CrumbRoute
          path="/project/:project/bulletin/:projectId/:bulletinId"
          component={LoadableBulletinBoard}
          title={t('navigation.bulletin_board')}
        />
        <CrumbRoute
          path="/project/:project/bulletin/:projectId"
          component={LoadableBulletinBoard}
          title={t('navigation.bulletin_board')}
        />
        <CrumbRoute
          path="/project/:project/timelines/:projectId"
          component={LoadableTimelines}
          title={t('navigation.timelines')}
        />
        <CrumbRoute
          path="/project/:projectKey/resources/:projectId"
          component={LoadableProjectResources}
          title={'Resurssit'}
        />
        <CrumbRoute
          path="/project/redirect/:project"
          component={LoadableProjectRedirect}
          title={t('permission.permissions')}
        />
        <CrumbRoute
          path="/project/:projectKey/filings/:projectId"
          component={LoadableFilingsDashboard}
          title={t('dashboard.notify')}
        />
        <CrumbRoute
          path="/project/:project/expensetracking/:projectId"
          component={LoadableTracking}
          title={t('dashboard.expense_tracking')}
        />
        <CrumbRoute
          path="/project/:projectKey/permissions/:projectId"
          component={LoadablePermissions}
          title={t('permissions.permissions-nav')}
        />
        <CrumbRoute
          path="/project/:projectKey/short-term-projects/:projectId"
          component={LoadableShortTermProjects}
          title={'Työtilaukset'}
        />
        <CrumbRoute
          exact
          path="/project/:projectKey/areaplanning/:projectId"
          component={LoadableAreaPlanning}
          title={'Aluesuunnittelu'}
        />
        <CrumbRoute
          path="/project/:projectKey/areaplanning/:projectId/:id"
          component={ImageCanvas}
          title={'Aluesuunnittelu'}
        />
        <CrumbRoute
          path="/project/:projectKey/email/"
          component={LoadableProjectEmail}
          title={t('navigation.email')}
        />
        <CrumbRoute
          path="/project/:projectKey/renolog/:projectId"
          component={LoadableRenolog}
          title={t('dashboard.sensors')}
        />
        <CrumbRoute
          path="/project/:projectKey/kanban/:projectId"
          component={LoadableKanban}
          title={t('kanban.title')}
        />
        <CrumbRoute
          path="/project/:project/:employer"
          component={LoadableProject}
          title={t('navigation.view_project')}
        />
        <CrumbRoute
          path="/project/:project"
          component={LoadableProjects}
          title={t('navigation.view_project')}
        />
        <CrumbRoute
          path="/user/:userId"
          component={LoadableUserProfile}
          title={'Käyttäjä'}
        />
        <CrumbRoute
          exact
          path="/buy-chatbot"
          component={BuyChatbot}
          title={'osta-nerobotti'}
        />
        <Route exact path="/admin" component={LoadableAdmin} />
        <CrumbRoute
          path="/admin/renolog"
          component={LoadableRenologAdmin}
          title={'Renolog'}
        />
        <CrumbRoute path="/admin/ai" component={LoadableAdminAi} title={'AI'} />
        <CrumbRoute
          exact
          path="/form-editor"
          component={FormEditorForms}
          title={'lomake-editori'}
        />
        <CrumbRoute
          path="/form-editor/:id"
          component={FormEditor}
          title={'lomake-editori'}
        />
      </Switch>
      <NotificationSnackbar />
      <Suspense fallback={null}>
        <ChatDialog />
      </Suspense>
      <Suspense fallback={null}>
        <NerobottiPanelHost />
      </Suspense>
      {showProjectEmailDialog ? (
        <Suspense fallback={null}>
          <ProjectEmailDialog />
        </Suspense>
      ) : null}
    </Layout>
  );
}

export default withRouter(App);
